import React, { useState } from "react";

type LegalModal = "terms" | "privacy" | null;

const useCases = [
  "Private DeFi",
  "Confidential trading",
  "Institutional settlement rails",
  "Shielded treasury management",
  "Privacy-enabled wallets",
  "B2B payment rails",
];

export default function LandingPage() {
  const [contactOpen, setContactOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState<LegalModal>(null);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`StealthOS enquiry from ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`,
    );
    window.location.href = `mailto:mark@stealthos.xyz?subject=${subject}&body=${body}`;
    setSent(true);
    setTimeout(() => {
      setContactOpen(false);
      setSent(false);
      setForm({ name: "", email: "", message: "" });
    }, 2000);
  };

  const handleLaunchAppClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
  };

  return (
    <div className="lp">
      {/* ─── NAVs ─── */}
      <header className="lp-nav">
        <div className="lp-nav-inner">
          <a href="/" className="lp-nav-brand">
            <img
              src="/stealthOS_logo-removebg-preview.png"
              alt="StealthOS"
              className="lp-nav-logo"
            />
          </a>
          <nav className="lp-nav-links">
            <a href="#how">How it works</a>
            <a href="#tech">Technology</a>
            <a href="#integrate">Integration</a>
            <a href="#why">Why StealthOS</a>
            <a href="/audits/">Audits</a>
            <a href="/waitlist">Join waitlist</a>
          </nav>
          <a href="/app" className="lp-btn lp-btn-ghost lp-nav-cta">
            Launch App
          </a>
        </div>
      </header>

      {/* ─── HERO ─── */}
      <section className="lp-hero">
        <div className="lp-hero-bg">
          <img
            src="/stealthOS logo.png"
            alt=""
            className="lp-hero-bg-img"
            aria-hidden="true"
          />
        </div>
        <div className="lp-hero-content">
          <h1 className="lp-hero-h1">
            Private transactions.
            <br />
            Any chain.
            <br />
            Enterprise-grade.
          </h1>
          <p className="lp-hero-sub">
            StealthOS is a ZK-powered chain agnostic privacy layer for
            programmable blockchains — enabling fully private DeFi operations,
            with ultra-fast, cost-efficient on-chain verification, while
            supporting regulatory compliance (KYC/AML) and seamless B2B
            integrations.
          </p>
          <div className="lp-hero-actions">
            <a href="/app" className="lp-btn lp-btn-primary">
              Launch App
            </a>
            <a href="#how" className="lp-btn lp-btn-outline">
              How it works
            </a>
          </div>
        </div>
      </section>

      {/* ─── STATS BAR ─── */}
      <div className="lp-statsbar">
        <div className="lp-statsbar-inner">
          <div className="lp-stat">
            <span className="lp-stat-value">Secure</span>
            <span className="lp-stat-label">Military-grade ZK layer</span>
          </div>
          <div className="lp-stat-divider" />
          <div className="lp-stat">
            <span className="lp-stat-value">Ultra-fast</span>
            <span className="lp-stat-label">Sub-second on-chain finality</span>
          </div>
          <div className="lp-stat-divider" />
          <div className="lp-stat">
            <span className="lp-stat-value">Low Cost</span>
            <span className="lp-stat-label">Minimal gas requirements</span>
          </div>
          <div className="lp-stat-divider" />
          <div className="lp-stat">
            <span className="lp-stat-value">Base & EVM</span>
            <span className="lp-stat-label">Chain coverage</span>
          </div>
        </div>
      </div>

      {/* ─── HOW IT WORKS ─── */}
      <section className="lp-section" id="how">
        <div className="lp-section-inner">
          <h2 className="lp-section-h2" style={{ paddingBottom: "32px" }}>
            Every move you make on-chain
            <br />
            is visible to your competitors. Until now.
          </h2>
          <p className="lp-section-sub">
            Every trade, swap, yield position, and transfer you execute is
            publicly readable — by competitors, front-runners, and anyone
            watching. StealthOS wraps your entire on-chain activity in a
            cryptographic privacy layer so your strategy stays yours.
          </p>
          <div className="lp-steps">
            <div className="lp-step">
              <div className="lp-step-num">01</div>
              <h3>Protect your positions.</h3>
              <p>
                Move any supported asset into the StealthOS ZK-powered privacy
                layer. Your balance, identity, and intent become
                cryptographically invisible on-chain.
              </p>
            </div>
            <div className="lp-step-arrow">→</div>
            <div className="lp-step">
              <div className="lp-step-num">02</div>
              <h3>Operate in stealth.</h3>
              <p>
                Execute any on-chain strategies without revealing who you are,
                who you're transacting with, or how much. Transactions are
                verified without exposing sensitive data.
              </p>
            </div>
            <div className="lp-step-arrow">→</div>
            <div className="lp-step">
              <div className="lp-step-num">03</div>
              <h3>Exit on your terms</h3>
              <p>
                You decide if, when, and how your assets return to public view.
                Unshield to any address at any time or stay private
                indefinitely. Nothing forces your hand, and nothing reveals your
                history.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CONFIDENTIAL LAYER ─── */}
      <section className="lp-section lp-section-alt">
        <div className="lp-section-inner">
          <h2
            className="lp-section-h2"
            style={{
              textAlign: "center",
              maxWidth: "100%",
              paddingBottom: "32px",
            }}
          >
            Privacy with native compliance —<br />
            built to support regulatory controls
          </h2>
          <div className="lp-two-col">
            <div className="lp-two-col-body">
              <p>
                StealthOS shields balances, transaction amounts, and participant
                identities across the full DeFi activity surface — transfers,
                swaps, trades, yield positions, and any on-chain operation —
                while embedding third-party wallet screening and compliance
                checks at entry and exit layers.
              </p>
              <p>
                Institutional and ecosystem partners get privacy across their
                entire on-chain footprint without sacrificing the regulatory
                safeguards their operations require. Compliance is a
                configurable layer, not an afterthought.
              </p>
            </div>
            <div className="lp-feature-list">
              <div className="lp-feature-item">
                Third-party wallet screening at entry and exit
              </div>
              <div className="lp-feature-item">
                Screens for OFAC sanctions, blacklisted wallets, and illicit
                funds
              </div>
              <div className="lp-feature-item">
                Compliance extension points for regulatory requirements
              </div>
              <div className="lp-feature-item">
                Screening hooks for institutional and ecosystem partners
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TECH ─── */}
      <section className="lp-section" id="tech">
        <div className="lp-section-inner">
          <h2 className="lp-section-h2" style={{ paddingBottom: "32px" }}>
            One of the most advanced ZK proof systems in production.
          </h2>
          <div className="lp-cards">
            <div className="lp-card">
              <h3>Nova IVC + Recursive Folding</h3>
              <p>
                Replaces legacy single-proof models. Each note operation folds
                into a running accumulator at constant size — enabling
                multi-step private computation and batching without linear
                verification costs.
              </p>
            </div>
            <div className="lp-card">
              <h3>Groth16 Compression</h3>
              <p>
                The Nova accumulator compresses to a fixed 256-byte Groth16
                proof verified in a single on-chain transaction. Verification
                cost stays near-constant even as usage and complexity scale.
              </p>
            </div>
            <div className="lp-card">
              <h3>TEE Attestation</h3>
              <p>
                Provers run inside Trusted Execution Environments. Every proof
                is signed with an attestation key registered on-chain, adding a
                hardware-backed trust anchor to the cryptographic guarantees.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── DEPLOYMENT MODELS ─── */}
      <section className="lp-section lp-section-alt" id="integrate">
        <div className="lp-section-inner">
          <h2 className="lp-section-h2" style={{ paddingBottom: "32px" }}>
            Three proving models.
            <br />
            One integration surface.
          </h2>
          <div className="lp-deploy-grid">
            <div className="lp-deploy-card">
              <div className="lp-deploy-tag">Client-Side WASM</div>
              <h3>In-browser proving</h3>
              <p>
                Proof generation runs entirely in the user's browser via
                WASM-compiled Rust. Secrets never leave the device. Ideal for
                wallet-native privacy integrations.
              </p>
            </div>
            <div className="lp-deploy-card lp-deploy-card-featured">
              <div className="lp-deploy-tag">Server-Side TEE</div>
              <h3>Enterprise prover</h3>
              <p>
                Heavy multi-input transactions are generated inside a Trusted
                Execution Environment. Scalable, auditable, and designed for
                institutional integrations at volume.
              </p>
            </div>
            <div className="lp-deploy-card">
              <div className="lp-deploy-tag">On-Chain Verification</div>
              <h3>Protocol-level privacy</h3>
              <p>
                Privacy verification embedded at the protocol layer. Enables
                privacy as a native on-chain primitive — composable with any
                dApp, wallet, or settlement rail.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── B2B INTEGRATION ─── */}
      <section className="lp-section">
        <div className="lp-section-inner">
          <h2 className="lp-section-h2" style={{ paddingBottom: "32px" }}>
            Plug-and-play privacy for any team building on-chain.
          </h2>
          <div className="lp-two-col">
            <div className="lp-two-col-body">
              <p>
                StealthOS turns advanced ZK cryptography into a modular,
                integration-ready privacy stack. Any dApp, wallet, or
                institutional partner can integrate without rebuilding their
                existing architecture.
              </p>
              <p>
                The SDK is designed for wallets, dApps, exchanges, and
                institutional rails — with chain-agnostic core circuits and an
                adaptable verification layer for any execution environment.
              </p>
              <div style={{ marginTop: "32px" }}>
                <a
                  href="#"
                  onClick={handleLaunchAppClick}
                  className="lp-btn lp-btn-primary"
                >
                  Start integrating
                </a>
              </div>
            </div>
            <div className="lp-feature-list">
              <div className="lp-feature-item">
                SDK-ready for wallets, dApps, and institutional rails
              </div>
              <div className="lp-feature-item">
                Modular architecture — integrate without rebuilding
              </div>
              <div className="lp-feature-item">
                Chain-agnostic core circuits
              </div>
              <div className="lp-feature-item">
                Verification layer adaptable to any execution environment
              </div>
              <div className="lp-feature-item">
                Extends to additional chains without redesigning core circuits
              </div>
              <div className="lp-feature-item">
                Production-ready on EVM (Base)
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHY ─── */}
      <section className="lp-section lp-section-alt" id="why">
        <div className="lp-section-inner">
          <h2
            className="lp-section-h2"
            style={{
              paddingBottom: "32px",
              textAlign: "center",
              maxWidth: "100%",
            }}
          >
            Most privacy protocols hit a wall.
            <br />
            StealthOS doesn't.
          </h2>
          <div className="lp-compare-table">
            <div className="lp-compare-header">
              <span />
              <span className="lp-compare-them">Legacy protocols</span>
              <span className="lp-compare-us">StealthOS</span>
            </div>
            {[
              ["Max notes per transaction", "2–4 (circuit-fixed)", "Unlimited"],
              ["On-chain verification cost", "Scales per proof", "Constant"],
              ["Proving time", "2–5 s (browser JS)", "100–500 ms (Rust)"],
              ["TEE attestation", "No", "Yes (Nitro / CVM)"],
              ["Proof system", "Fixed Groth16 / PLONK", "Nova IVC + Groth16"],
              ["Compliance hooks", "None", "Built-in screening layer"],
              ["B2B SDK", "No", "Integration-ready"],
            ].map(([feature, them, us]) => (
              <div key={feature} className="lp-compare-row">
                <span className="lp-compare-feature">{feature}</span>
                <span className="lp-compare-them-val">{them}</span>
                <span className="lp-compare-us-val">{us}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CREDIBILITY ─── */}
      <section className="lp-section">
        <div className="lp-section-inner">
          <h2 className="lp-section-h2" style={{ paddingBottom: "32px" }}>
            Built for enterprise and institutional-grade privacy deployments.
          </h2>
          <div className="lp-cards">
            <div className="lp-card">
              <h3>Cross-environment consistency</h3>
              <p>
                Cryptographic operations are consistent across Rust, WASM,
                Circom, and on-chain programs — a non-trivial engineering
                requirement that most teams underestimate.
              </p>
            </div>
            <div className="lp-card">
              <h3>Full parameter pipeline</h3>
              <p>
                Nova public parameters → Groth16 compression keys → on-chain
                verification constants. The full trusted setup and key
                generation pipeline is owned end-to-end.
              </p>
            </div>
            <div className="lp-card">
              <h3>Audited and enterprise-reviewed</h3>
              <p>
                Infrastructure hardened by security audits and enterprise review
                cycles. A production-ready system built to run at institutional
                scale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── USE CASES ─── */}
      <section className="lp-section lp-section-alt">
        <div className="lp-section-inner">
          <h2 className="lp-section-h2" style={{ paddingBottom: "32px" }}>
            Built for teams that take privacy seriously.
          </h2>
          <div className="lp-usecases">
            {useCases.map((uc) => (
              <div key={uc} className="lp-usecase">
                {uc}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA BAND ─── */}
      <section className="lp-cta-band">
        <div className="lp-cta-band-inner">
          <h2>Ready to build with private transaction infrastructure?</h2>
          <p>
            Stop broadcasting your strategy to competitors. Trade privately with
            StealthOS.
          </p>
          <a
            href="#"
            onClick={handleLaunchAppClick}
            className="lp-btn lp-btn-primary lp-btn-lg"
          >
            Enter StealthOS
          </a>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <span>StealthOS</span>
          </div>
          <p className="lp-footer-copy">
            © StealthOS 2026. All rights reserved.
          </p>
          <div className="lp-footer-legal">
            <button
              className="lp-footer-link"
              onClick={() => setLegalOpen("terms")}
            >
              Terms
            </button>
            <span className="lp-footer-sep">·</span>
            <button
              className="lp-footer-link"
              onClick={() => setLegalOpen("privacy")}
            >
              Privacy Policy
            </button>
            <span className="lp-footer-sep">·</span>
            <a className="lp-footer-link" href="/audits/">
              Audits
            </a>
            <span className="lp-footer-sep">·</span>
            <button
              className="lp-footer-link"
              onClick={() => setContactOpen(true)}
            >
              Contact
            </button>
          </div>
        </div>
      </footer>

      {/* ─── CONTACT MODAL ─── */}
      {contactOpen && (
        <div className="lp-modal-overlay" onClick={() => setContactOpen(false)}>
          <div className="lp-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="lp-modal-close"
              onClick={() => setContactOpen(false)}
            >
              ✕
            </button>
            <h2>Get in touch</h2>
            <p className="lp-modal-sub">
              Reach out about integrations, partnerships, or enterprise access.
            </p>
            {sent ? (
              <div className="lp-modal-sent">Opening your email client...</div>
            ) : (
              <form onSubmit={handleSubmit} className="lp-contact-form">
                <div className="lp-form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    required
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </div>
                <div className="lp-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    required
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, email: e.target.value }))
                    }
                  />
                </div>
                <div className="lp-form-group">
                  <label>Message</label>
                  <textarea
                    required
                    rows={5}
                    placeholder="Tell us what you're building..."
                    value={form.message}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, message: e.target.value }))
                    }
                  />
                </div>
                <button
                  type="submit"
                  className="lp-btn lp-btn-primary"
                  style={{ width: "100%", marginTop: "8px" }}
                >
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ─── LEGAL MODAL ─── */}
      {legalOpen && (
        <div className="lp-modal-overlay" onClick={() => setLegalOpen(null)}>
          <div
            className="lp-modal lp-modal-legal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="lp-modal-close"
              onClick={() => setLegalOpen(null)}
            >
              ✕
            </button>
            {legalOpen === "terms" ? (
              <>
                <h2>Terms &amp; Conditions</h2>
                <p className="lp-modal-sub">Last updated: March 2026</p>
                <div className="lp-legal-body">
                  <p>
                    By accessing or using StealthOS, you agree to be bound by
                    these Terms and Conditions. Please read them carefully
                    before using the platform.
                  </p>
                  <h3>1. Use of the Platform</h3>
                  <p>
                    StealthOS provides privacy infrastructure for on-chain
                    transactions. You may only use this platform for lawful
                    purposes and in accordance with applicable laws and
                    regulations in your jurisdiction.
                  </p>
                  <h3>2. No Financial Advice</h3>
                  <p>
                    Nothing on this platform constitutes financial, investment,
                    legal, or tax advice. You are solely responsible for your
                    decisions and transactions.
                  </p>
                  <h3>3. Compliance</h3>
                  <p>
                    You agree not to use StealthOS to facilitate any activity
                    that violates applicable anti-money laundering (AML),
                    know-your-customer (KYC), or sanctions laws. StealthOS
                    includes built-in wallet screening and compliance hooks for
                    institutional partners.
                  </p>
                  <h3>4. No Warranty</h3>
                  <p>
                    The platform is provided "as is" without warranty of any
                    kind. StealthOS makes no representations regarding uptime,
                    fitness for a particular purpose, or freedom from errors.
                  </p>
                  <h3>5. Limitation of Liability</h3>
                  <p>
                    To the maximum extent permitted by law, StealthOS shall not
                    be liable for any indirect, incidental, special, or
                    consequential damages arising from use of the platform.
                  </p>
                  <h3>6. Changes</h3>
                  <p>
                    We reserve the right to update these terms at any time.
                    Continued use of the platform after changes constitutes
                    acceptance of the updated terms.
                  </p>
                  <h3>7. Contact</h3>
                  <p>
                    Questions regarding these terms can be directed to{" "}
                    <a href="mailto:mark@stealthos.xyz">mark@stealthos.xyz</a>.
                  </p>
                </div>
              </>
            ) : (
              <>
                <h2>Privacy Policy</h2>
                <p className="lp-modal-sub">Last updated: March 2026</p>
                <div className="lp-legal-body">
                  <p>
                    StealthOS is built on the principle that financial privacy
                    is a right. This policy explains how we handle information
                    when you use our platform.
                  </p>
                  <h3>1. Information We Do Not Collect</h3>
                  <p>
                    StealthOS does not collect, store, or transmit your private
                    keys, spending keys, or shielded note data. Proof generation
                    can occur entirely client-side in your browser.
                  </p>
                  <h3>2. On-Chain Data</h3>
                  <p>
                    By design, StealthOS obscures on-chain transaction details
                    including balances, amounts, and participant identities
                    using zero-knowledge proofs. Only constant-size commitments
                    are published on-chain.
                  </p>
                  <h3>3. Analytics &amp; Cookies</h3>
                  <p>
                    This website may collect anonymised usage analytics (e.g.
                    page views) to improve the product. No personally
                    identifiable information is linked to analytics data.
                  </p>
                  <h3>4. Third-Party Integrations</h3>
                  <p>
                    Institutional deployments may include third-party wallet
                    screening providers for compliance purposes. Any data shared
                    with such providers is governed by their respective privacy
                    policies.
                  </p>
                  <h3>5. Data Security</h3>
                  <p>
                    We implement industry-standard security practices. However,
                    no system is completely secure, and we cannot guarantee
                    absolute security of information transmitted to our
                    platform.
                  </p>
                  <h3>6. Changes</h3>
                  <p>
                    We may update this policy as the platform evolves. Material
                    changes will be communicated via this page.
                  </p>
                  <h3>7. Contact</h3>
                  <p>
                    For privacy-related inquiries, contact{" "}
                    <a href="mailto:mark@stealthos.xyz">mark@stealthos.xyz</a>.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
